import React from "react";
import ButtonUnstyled, {
  ButtonProps as ButtonUnstyledProps,
} from "@mui/base/Button";
import styled from "@emotion/styled";
import { CircularProgress } from "@mui/material";

const LoadingSpinner = styled(CircularProgress)({
  color: "inherit",
  padding: "4px",
});

type ButtonVariant =
  | "solid"
  | "text"
  | "outline"
  | "ghost"
  | "secondary"
  | "danger";
type ButtonSize = "sm" | "md" | "lg";

function variantClass(x: ButtonVariant): string {
  switch (x) {
    case "solid":
      return "";
    case "outline":
      return "btn-outline";
    case "text":
      return "btn-link";
    case "ghost":
      return "btn-ghost";
    case "secondary":
      return "btn-secondary";
    case "danger":
      return "btn-danger";
  }
}

function sizeClass(x: ButtonSize): string {
  switch (x) {
    case "sm":
      return "btn-sm";
    case "md":
      return "";
    case "lg":
      return "btn-lg";
  }
}

type CommonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};
type ConditionalProps =
  | (React.AnchorHTMLAttributes<HTMLAnchorElement> & {
      component: "a";
    })
  | (Omit<ButtonUnstyledProps, "component"> & {
      component?: "button" | undefined;
    });
export type ButtonProps = CommonProps & ConditionalProps;

export type ButtonTypedButtonProps = Extract<
  ButtonProps,
  { component?: "button" | undefined }
>;

const Button = ({
  variant = "solid",
  loading,
  size = "md",
  ...props
}: ButtonProps) => {
  const content = loading ? (
    <LoadingSpinner
      style={{
        height: `26px`,
        width: `26px`,
      }}
    />
  ) : (
    props.children
  );

  const classes = [
    `${props.className ?? ""} btn`,
    variantClass(variant),
    sizeClass(size),
  ].join(" ");

  if (props.component === "a") {
    const { component, ...attrs } = props;
    return (
      <a {...attrs} className={classes}>
        {content}
      </a>
    );
  } else {
    const { component, disabled, ...attrs } = props;
    return (
      <ButtonUnstyled
        {...attrs}
        className={classes}
        disabled={disabled || loading}
      >
        {content}
      </ButtonUnstyled>
    );
  }
};

export { Button };
